//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { FieldArrayWithId, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { DEFAULT_VARIATION } from '@campaigns/components/shared/product-details-modal/tabs/VariantsTab';
import { VariantRow } from '@campaigns/components/shared/product-details-modal/variants/VariantRow';
import { VariantsHeader } from '@campaigns/components/shared/product-details-modal/variants/VariantsHeader';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { TableHeaderCellText } from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { Table } from '@components/organisms/table/Table';

interface VariantsTableProps {
  fields: FieldArrayWithId<EditableBVSamplingProduct, 'variations'>[];
  isViewOnly: boolean;
  onRemoveVariant: (index: number) => void;
}

export const VariantsTable = ({
  fields,
  isViewOnly,
  onRemoveVariant,
}: VariantsTableProps) => {
  const { getValues, reset } = useFormContext<EditableBVSamplingProduct>();
  const { removeVariantImage } = useProductModal();

  const handleRemoveAllVariants = () => {
    const currentValues = getValues();
    const firstVariation = fields[0];

    for (let index = fields.length - 1; index > 0; index -= 1) {
      removeVariantImage(index);
    }

    reset({
      ...currentValues,
      variations: [firstVariation ?? DEFAULT_VARIATION],
    });
  };

  return (
    <Container>
      <VariantsHeader
        isViewOnly={isViewOnly}
        onRemoveAll={handleRemoveAllVariants}
      />
      <StyledTable
        body={fields.map((field, index) => (
          <VariantRow
            index={index}
            isViewOnly={isViewOnly}
            key={field.id}
            onRemove={() => onRemoveVariant(index)}
            totalVariations={fields.length}
          />
        ))}
        headerCells={[
          <TableHeaderCellText key='IMAGE'>Image</TableHeaderCellText>,
          <TableHeaderCellText key='NAME'>Name</TableHeaderCellText>,
          <SmallTooltip
            content="Enter the number of product units you're making available for this campaign. Count multipacks as single items."
            key='QUANTITY'
            target={<TableHeaderCellText>Quantity</TableHeaderCellText>}
          />,
          <SmallTooltip
            content='Enter a fair market value that reflects the normal price of the product on your website or other retail sites.'
            key='FMV'
            target={<TableHeaderCellText>FMV</TableHeaderCellText>}
          />,
          <SmallTooltip
            content='Enter a GTIN, EAN, or UPC to help us identify your product.'
            key='GTIN'
            target={
              <TableHeaderCellText>Barcode Number (GTIN)</TableHeaderCellText>
            }
          />,
          <TableHeaderCellText key='ACTIONS'></TableHeaderCellText>,
        ]}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;

const StyledTable = styled(Table)`
  margin-bottom: ${({ theme }) => theme.spacings[4]};
`;
